import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';

/**
 * `CustomerPaymentMethod` — tokenised payment method. Mirrors the
 * schema in BACKEND_AGENT_PROMPTS.md Item 5. The PAN never touches our
 * origin: cards arrive as gateway tokens via the
 * `CardSetupIntent` → iframe → `Confirm` flow; UPI VPAs are public
 * identifiers and persisted directly.
 */
export type PaymentMethodKind = 'card' | 'upi';

export interface CustomerPaymentMethod {
  paymentMethodId?: string;
  kind: PaymentMethodKind;
  /** Card-only — populated on `confirmCard()`. NEVER includes the PAN. */
  brand?: 'Visa' | 'Mastercard' | 'RuPay' | 'AmEx' | string;
  last4?: string;
  expiryMonth?: number;
  expiryYear?: number;
  /** UPI-only. */
  vpa?: string;
  isDefault?: boolean;
  isActive?: boolean;
  createdAt?: string;
}

export interface CardSetupIntent {
  /** Gateway client secret (Razorpay / Stripe / etc) — used to mount the iframe. */
  clientSecret: string;
  gatewayProvider: string;
}

export interface PaymentMethodService {
  /** `POST /api/auth/me/payment-methods` — list. Tokens never returned. */
  mine(): Promise<CustomerPaymentMethod[]>;

  /**
   * `POST /api/auth/me/payment-methods/CardSetupIntent` — start the
   * tokenisation flow. Returns the gateway's one-time client secret
   * for mounting the iframe that collects the card.
   */
  startCardSetup(): Promise<CardSetupIntent>;

  /**
   * `POST /api/auth/me/payment-methods/Confirm` — finalises a card
   * row after the gateway confirms tokenisation. Body is whatever the
   * iframe handed back (provider, token, brand, last4, expiry).
   */
  confirmCard(body: {
    provider: string;
    gatewayToken: string;
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  }): Promise<CustomerPaymentMethod>;

  /** `POST /api/auth/me/payment-methods/AddUpi { Vpa }`. */
  addUpi(vpa: string): Promise<CustomerPaymentMethod>;

  /** `POST /api/auth/me/payment-methods/SetDefault { PaymentMethodId }`. */
  setDefault(paymentMethodId: string): Promise<void>;

  /** `POST /api/auth/me/payment-methods/Delete { PaymentMethodId }`. */
  remove(paymentMethodId: string): Promise<void>;
}

export function createPaymentMethodService(
  client: AxiosInstance,
): PaymentMethodService {
  return {
    mine: () =>
      client
        .post<
          ObjectResponse<CustomerPaymentMethod[] | { items?: CustomerPaymentMethod[] }>
        >('/auth/me/payment-methods', {})
        .then((r) => unwrapItems<CustomerPaymentMethod>(r.data)),

    startCardSetup: () =>
      client
        .post<ObjectResponse<CardSetupIntent>>(
          '/auth/me/payment-methods/CardSetupIntent',
          {},
        )
        .then((r) => unwrap(r.data)),

    confirmCard: (body) =>
      client
        .post<ObjectResponse<CustomerPaymentMethod>>(
          '/auth/me/payment-methods/Confirm',
          {
            Provider: body.provider,
            GatewayToken: body.gatewayToken,
            Brand: body.brand,
            Last4: body.last4,
            ExpiryMonth: body.expiryMonth,
            ExpiryYear: body.expiryYear,
          },
        )
        .then((r) => unwrap(r.data)),

    addUpi: (vpa) =>
      client
        .post<ObjectResponse<CustomerPaymentMethod>>(
          '/auth/me/payment-methods/AddUpi',
          { Vpa: vpa },
        )
        .then((r) => unwrap(r.data)),

    setDefault: (paymentMethodId) =>
      client
        .post<ObjectResponse<null>>(
          '/auth/me/payment-methods/SetDefault',
          { PaymentMethodId: paymentMethodId },
        )
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),

    remove: (paymentMethodId) =>
      client
        .post<ObjectResponse<null>>(
          '/auth/me/payment-methods/Delete',
          { PaymentMethodId: paymentMethodId },
        )
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),
  };
}
